import React, { useEffect, useId, useState } from "react";
import { AppButton, AppModal, Checkbox, Callout, FormField, Input, NativeSelect, Textarea } from "../../ui";
import StudentAccessPicker from "../../ContentProtection/StudentAccessPicker";
import { MATERIAL_CATEGORIES, isVideoFile } from "./courseDetails";

const formFrom = (m) => ({
  title: m?.title || "",
  topic: m?.topic || "",
  description: m?.description || "",
  category: m?.category || "other",
  isPreview: !!m?.isPreview,
  // The file's OWN setting; a file that never chose one has always been downloadable.
  studentAccess: m?.studentAccess || "download",
});

/**
 * EditMaterialModal — rename a course file, file it under a topic and type,
 * and decide what students may do with it: a document's access level, or
 * whether a video is a free preview for students who have not enrolled.
 *
 * Props:
 *   material — the file being edited, or null when closed
 *   onHide
 *   onSave(body) — resolves when saved; throws to keep the dialog open
 */
const EditMaterialModal = ({ material, onHide, onSave }) => {
  const formId = useId();
  const [form, setForm] = useState(() => formFrom(material));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(formFrom(material));
    setError("");
    setSaving(false);
  }, [material]);

  const isVideo = isVideoFile(material);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const title = form.title.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!title || saving) return;
    const body = {
      title,
      topic: form.topic.trim(),
      description: form.description.trim(),
    };
    if (isVideo) body.isPreview = form.isPreview;
    else {
      body.category = form.category;
      body.studentAccess = form.studentAccess;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(body);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the changes.");
      setSaving(false);
    }
  };

  const close = () => { if (!saving) onHide(); };

  return (
    <AppModal
      show={!!material}
      onHide={close}
      title={isVideo ? "Edit video" : "Edit material"}
      size="sm"
      closeOnOverlay={!saving}
      footer={(
        <>
          <AppButton variant="outline" onClick={close} disabled={saving}>Cancel</AppButton>
          <AppButton type="submit" form={formId} disabled={saving || !title}>
            {saving ? "Saving…" : "Save changes"}
          </AppButton>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} className="tw-flex tw-flex-col tw-gap-4" noValidate>
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Title" required error={form.title && !title ? "Enter a title." : undefined}>
          <Input value={form.title} onChange={set("title")} disabled={saving} maxLength={200} autoFocus />
        </FormField>

        {!isVideo && (
          <FormField label="Type">
            <NativeSelect value={form.category} onChange={set("category")} disabled={saving}>
              {MATERIAL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </NativeSelect>
          </FormField>
        )}

        <FormField label="Topic" hint="Which part of the course this belongs to.">
          <Input value={form.topic} onChange={set("topic")} disabled={saving} maxLength={200} />
        </FormField>

        <FormField label="Description">
          <Textarea value={form.description} onChange={set("description")} disabled={saving} rows={3} />
        </FormField>

        {isVideo ? (
          <Checkbox
            label="Free preview"
            description="Students who have not enrolled can watch this video from the course page."
            checked={form.isPreview}
            onChange={(e) => setForm((f) => ({ ...f, isPreview: e.target.checked }))}
            disabled={saving}
          />
        ) : (
          <StudentAccessPicker
            value={form.studentAccess}
            onChange={(studentAccess) => setForm((f) => ({ ...f, studentAccess }))}
            fileType={material?.fileType}
            disabled={saving}
          />
        )}
      </form>
    </AppModal>
  );
};

export default EditMaterialModal;
